import { TokenValidationResult } from '../types/authTypes';
import { logger } from 'utils/logger';

/**
 * Utilities for JWT token manipulation and validation
 */
export class TokenUtils {
  /**
   * Decodes a JWT token without verifying the signature
   * @param token - JWT token
   * @returns Decoded payload or null if invalid
   */
  static decodeJWT(token: string): any | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      logger.error('[TokenUtils] Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Validates if a JWT token is valid and not expired
   * @param token - JWT token
   * @returns Validation result
   */
  static validateToken(token: string): TokenValidationResult {
    try {
      if (!token) {
        return {
          isValid: false,
          isExpired: true,
        };
      }

      const payload = this.decodeJWT(token);
      if (!payload) {
        return {
          isValid: false,
          isExpired: true,
        };
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = payload.exp || payload.expires_at;

      if (!expiresAt) {
        return {
          isValid: true,
          isExpired: false,
        };
      }

      const isExpired = now >= expiresAt;
      const timeUntilExpiry = expiresAt - now;

      return {
        isValid: !isExpired,
        expiresAt: expiresAt * 1000, // Convert to milliseconds
        isExpired,
        timeUntilExpiry: timeUntilExpiry > 0 ? timeUntilExpiry : 0,
      };
    } catch (error) {
      logger.error('[TokenUtils] Error validating token:', error);
      return {
        isValid: false,
        isExpired: true,
      };
    }
  }

  /**
   * Checks if a token is near expiry
   * @param token - JWT token
   * @param thresholdSeconds - Seconds before expiry to consider "near expiry"
   * @returns true if near expiry
   */
  static isTokenNearExpiry(token: string, thresholdSeconds: number = 300): boolean {
    const validation = this.validateToken(token);

    if (!validation.isValid || !validation.timeUntilExpiry) {
      return true;
    }

    return validation.timeUntilExpiry <= thresholdSeconds;
  }

  /**
   * Calculates the remaining time of a token in a human-readable format
   * @param token - JWT token
   * @returns Formatted string with remaining time
   */
  static getTokenTimeRemaining(token: string): string {
    const validation = this.validateToken(token);

    if (!validation.isValid || !validation.timeUntilExpiry) {
      return 'Expired';
    }

    const hours = Math.floor(validation.timeUntilExpiry / 3600);
    const minutes = Math.floor((validation.timeUntilExpiry % 3600) / 60);
    const seconds = validation.timeUntilExpiry % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Extracts user information from a JWT token
   * @param token - JWT token
   * @returns User information or null
   */
  static extractUserInfo(token: string): {
    userId?: string;
    email?: string;
    roles?: string[];
    permissions?: string[];
  } | null {
    try {
      const payload = this.decodeJWT(token);
      if (!payload) {
        return null;
      }

      return {
        userId: payload.sub || payload.user_id || payload.userId,
        email: payload.email || payload.email_address,
        roles: payload.roles || payload.role || [],
        permissions: payload.permissions || payload.permission || [],
      };
    } catch (error) {
      logger.error('[TokenUtils] Error extracting user information:', error);
      return null;
    }
  }

  /**
   * Checks if a token has a specific role
   * @param token - JWT token
   * @param role - Role to check
   * @returns true if the token has the role
   */
  static hasRole(token: string, role: string): boolean {
    const userInfo = this.extractUserInfo(token);
    if (!userInfo || !userInfo.roles) {
      return false;
    }

    return userInfo.roles.includes(role);
  }

  /**
   * Checks if a token has a specific permission
   * @param token - JWT token
   * @param permission - Permission to check
   * @returns true if the token has the permission
   */
  static hasPermission(token: string, permission: string): boolean {
    const userInfo = this.extractUserInfo(token);
    if (!userInfo || !userInfo.permissions) {
      return false;
    }

    return userInfo.permissions.includes(permission);
  }

  /**
   * Generates an expiry timestamp based on a number of seconds from now
   * @param secondsFromNow - Seconds from now
   * @returns Timestamp in milliseconds
   */
  static generateExpiryTimestamp(secondsFromNow: number): number {
    return Date.now() + secondsFromNow * 1000;
  }

  /**
   * Checks if a timestamp has already expired
   * @param timestamp - Timestamp in milliseconds
   * @returns true if expired
   */
  static isTimestampExpired(timestamp: number): boolean {
    return Date.now() >= timestamp;
  }
}
